import { useState, type FormEvent } from "react";
import { callFetchEndpoint, type FetchTestResult } from "../api";
import { useLocalStorage } from "../hooks/useLocalStorage";
import type { ApiType, FetchRequestBody } from "../types";
import { Section } from "../components/Section";
import { DataSourceBadge, StatusBadge } from "../components/Badges";
import { fmtMs } from "../format";

interface StoredCreds {
  brand: string;
  apiKey: string;
}

const DEFAULT_PAYLOAD: Record<ApiType, string> = {
  PAN: "{}",
  UAN: "{}",
  CRIF: JSON.stringify(
    {
      lead_id: "test-1",
      mobile: "9876543210",
      dob: "1990-01-15",
      first_name: "TEST",
      last_name: "USER",
      address: {
        address: "B-12, Sector 5",
        city: "New Delhi",
        state: "Delhi",
        pincode: "110085",
      },
    },
    null,
    2,
  ),
};

export const TesterPage = () => {
  const [creds, setCreds] = useLocalStorage<StoredCreds>("dashboard.creds", {
    brand: "DUNIYA",
    apiKey: "",
  });

  const [apiType, setApiType] = useState<ApiType>("PAN");
  const [pan, setPan] = useState("BFLPS3010M");
  const [ttlDays, setTtlDays] = useState(30);
  const [payload, setPayload] = useState<string>(DEFAULT_PAYLOAD.PAN);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<FetchTestResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const updateApiType = (next: ApiType) => {
    setApiType(next);
    setPayload(DEFAULT_PAYLOAD[next]);
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParseError(null);

    let parsed: Record<string, unknown> = {};
    try {
      parsed = payload.trim() ? JSON.parse(payload) : {};
    } catch (err) {
      setParseError(`payload is not valid JSON: ${(err as Error).message}`);
      return;
    }

    const body: FetchRequestBody = {
      brand_id: creds.brand.toUpperCase(),
      pan: pan.toUpperCase(),
      api_type: apiType,
      payload: parsed,
      cache_ttl_days: Number(ttlDays) || 0,
    };

    setSubmitting(true);
    try {
      const res = await callFetchEndpoint(body, creds);
      setResult(res);
    } catch (err) {
      setResult({
        status: 0,
        envelope: {
          status_code: 0,
          success: false,
          code: "NETWORK_ERROR",
          message: (err as Error).message,
          cache_hit: false,
          data_source: null,
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const envelope = result?.envelope;
  const success = envelope?.success === true;

  return (
    <>
      <Section
        title="Test the unified /api/v1/fetch endpoint"
        description="Brand creds are kept in localStorage on this device only — never sent anywhere besides the Central API."
      >
        <form className="form" onSubmit={onSubmit}>
          <div className="grid grid-2">
            <label className="field">
              <span>Brand</span>
              <input
                className="input"
                placeholder="x-brand"
                value={creds.brand}
                onChange={(e) => setCreds({ ...creds, brand: e.target.value })}
                required
              />
            </label>
            <label className="field">
              <span>API key</span>
              <input
                className="input"
                placeholder="x-api-key"
                value={creds.apiKey}
                onChange={(e) => setCreds({ ...creds, apiKey: e.target.value })}
                type="password"
                required
              />
            </label>
          </div>

          <div className="grid grid-3 grid-margin">
            <label className="field">
              <span>api_type</span>
              <select
                className="input"
                value={apiType}
                onChange={(e) => updateApiType(e.target.value as ApiType)}
              >
                <option value="PAN">PAN</option>
                <option value="CRIF">CRIF</option>
                <option value="UAN">UAN</option>
              </select>
            </label>
            <label className="field">
              <span>pan</span>
              <input
                className="input"
                placeholder="ABCDE1234F"
                value={pan}
                onChange={(e) => setPan(e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span>cache_ttl_days</span>
              <input
                className="input"
                type="number"
                min={0}
                max={365}
                value={ttlDays}
                onChange={(e) => setTtlDays(Number(e.target.value))}
              />
            </label>
          </div>

          <label className="field grid-margin">
            <span>payload (JSON)</span>
            <textarea
              className="input textarea"
              rows={apiType === "CRIF" ? 12 : 4}
              spellCheck={false}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
            />
          </label>

          {parseError && <div className="error-banner">{parseError}</div>}

          <div className="row gap-8 grid-margin">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Sending…" : "POST /api/v1/fetch"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setPayload(DEFAULT_PAYLOAD[apiType])}
            >
              Reset payload
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setResult(null)}
              disabled={!result}
            >
              Clear result
            </button>
          </div>
        </form>
      </Section>

      {result && envelope && (
        <Section title="Response">
          <div className="row gap-12 wrap">
            <StatusBadge code={result.status} />
            <DataSourceBadge source={envelope.data_source} />
            <span className="dim small">
              cache_hit: <strong>{String(envelope.cache_hit)}</strong>
            </span>
            {success && "response" in envelope && envelope.response?.cache_age_ms != null && (
              <span className="dim small">
                cache_age: <strong>{fmtMs(envelope.response.cache_age_ms)}</strong>
              </span>
            )}
            {success && "response" in envelope && envelope.response?.provider && (
              <span className="dim small">
                provider: <strong>{envelope.response.provider}</strong>
              </span>
            )}
          </div>

          <pre className="code-block">
            {JSON.stringify(envelope, null, 2)}
          </pre>
        </Section>
      )}
    </>
  );
};
